import FileSaver from 'file-saver';
import _ from 'lodash';
import moment from 'moment';
import * as RestService from '../../_config/rest';
import {
  APPEND_POWER_CONTROL_UNIT_EVENTS,
  APPEND_POWER_CONTROL_UNITS,
  RESET_POWER_CONTROL_UNIT_EVENTS_PAGINATION_DATA,
  RESET_POWER_CONTROL_UNITS_FILTERS,
  RESET_POWER_CONTROL_UNITS_PAGINATION_DATA,
  SAVE_POWER_CONTROL_UNIT_DEVICES,
  SAVE_POWER_CONTROL_UNIT_EVENTS,
  SAVE_POWER_CONTROL_UNITS,
  SAVE_POWER_CONTROL_UNITS_ALERTS,
  SAVE_POWER_CONTROL_UNITS_ENERGY_SUMMARY,
  SAVE_POWER_CONTROL_UNITS_OVERVIEW,
  SAVE_POWER_CONTROL_UNITS_POWER_ON_HOURS,
  SELECT_POWER_CONTROL_UNIT,
  SET_POWER_CONTROL_UNIT_EVENTS_FILTER,
  SET_POWER_CONTROL_UNIT_EVENTS_PAGINATION_DATA,
  SET_POWER_CONTROL_UNIT_OPERATIONAL_MODE,
  SET_POWER_CONTROL_UNITS_FILTER,
  SET_POWER_CONTROL_UNITS_ORDER,
  SET_POWER_CONTROL_UNITS_PAGINATION_DATA,
  UPDATE_POWER_CONTROL_UNIT,
} from './actionTypes/powerControlUnits';

// Sync action creators
export function savePowerControlUnits(pcus) {
  return {
    type: SAVE_POWER_CONTROL_UNITS,
    payload: pcus,
  };
}

export function appendPowerControlUnits(pcus) {
  return {
    type: APPEND_POWER_CONTROL_UNITS,
    payload: pcus,
  };
}

export function updatePowerControlUnitOnState(pcu) {
  return {
    type: UPDATE_POWER_CONTROL_UNIT,
    payload: pcu,
  };
}

export function selectPowerControlUnit(pcu) {
  return {
    type: SELECT_POWER_CONTROL_UNIT,
    payload: pcu,
  };
}

export function setOperationalMode(value) {
  return {
    type: SET_POWER_CONTROL_UNIT_OPERATIONAL_MODE,
    payload: value,
  };
}

export function setPowerControlUnitsPaginationData(pagination) {
  return {
    type: SET_POWER_CONTROL_UNITS_PAGINATION_DATA,
    payload: pagination,
  };
}

export function resetPowerControlUnitsPaginationData() {
  return {
    type: RESET_POWER_CONTROL_UNITS_PAGINATION_DATA,
  };
}

export function setPowerControlUnitsFilter(name, value) {
  return {
    type: SET_POWER_CONTROL_UNITS_FILTER,
    payload: { name, value },
  };
}

export function resetPowerControlUnitsFilters() {
  return {
    type: RESET_POWER_CONTROL_UNITS_FILTERS,
  };
}

export function setPowerControlUnitsOrder(orderBy, orderDir) {
  return {
    type: SET_POWER_CONTROL_UNITS_ORDER,
    payload: { orderBy, orderDir },
  };
}

export function savePowerControlUnitEvents(events) {
  return {
    type: SAVE_POWER_CONTROL_UNIT_EVENTS,
    payload: events,
  };
}

export function appendPowerControlUnitEvents(events) {
  return {
    type: APPEND_POWER_CONTROL_UNIT_EVENTS,
    payload: events,
  };
}

export function setPowerControlUnitEventsPaginationData(pagination) {
  return {
    type: SET_POWER_CONTROL_UNIT_EVENTS_PAGINATION_DATA,
    payload: pagination,
  };
}

export function resetPowerControlUnitEventsPaginationData() {
  return {
    type: RESET_POWER_CONTROL_UNIT_EVENTS_PAGINATION_DATA,
  };
}

export function setPowerControlUnitEventsFilter(name, value) {
  return {
    type: SET_POWER_CONTROL_UNIT_EVENTS_FILTER,
    payload: { name, value },
  };
}

export function savePowerControlUnitsOverview(overview) {
  return {
    type: SAVE_POWER_CONTROL_UNITS_OVERVIEW,
    payload: overview,
  };
}

export function savePowerControlUnitsPowerOnHours(powerOnHours) {
  return {
    type: SAVE_POWER_CONTROL_UNITS_POWER_ON_HOURS,
    payload: powerOnHours,
  };
}

export function savePowerControlUnitsEnergySummary(energySummary) {
  return {
    type: SAVE_POWER_CONTROL_UNITS_ENERGY_SUMMARY,
    payload: energySummary,
  };
}

export function savePowerControlUnitsAlerts(alerts) {
  return {
    type: SAVE_POWER_CONTROL_UNITS_ALERTS,
    payload: alerts,
  };
}

export function savePowerControlUnitDevices(devices) {
  return {
    type: SAVE_POWER_CONTROL_UNIT_DEVICES,
    payload: devices,
  };
}

// Async thunks
export function fetchPowerControlUnits(page = 0, append = false, pageSize = 1000) {
  return async (dispatch, getState) => {
    try {
      const filters = structuredClone(getState().powerControlUnits.data.filters);
      const params = _.pick(
        { page, pageSize, ...filters },
        ['page', 'pageSize', 'state', 'gatewayId', 'automationEnabled'],
      );

      const response = await RestService.fetchPowerControlUnits(params);
      
      if (response.data && response.data.content) {
        dispatch(append ? appendPowerControlUnits(response.data.content) : savePowerControlUnits(response.data.content));
        dispatch(setPowerControlUnitsPaginationData(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

// Unfiltered snapshot of every unit, used by the overview without touching the list state
export function fetchAllPowerControlUnits(pageSize = 1000) {
  return async () => {
    const response = await RestService.fetchPowerControlUnits({ page: 0, pageSize });
    if (!response.data || !response.data.content) throw new Error();
    return response.data.content;
  };
}

export function fetchPowerControlUnit(id) {
  return async (dispatch) => {
    try {
      const response = await RestService.fetchPowerControlUnit(id);
      if (response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function createPowerControlUnit(data) {
  return async (dispatch) => {
    try {
      const response = await RestService.createPowerControlUnit(data);
      if (response.data) {
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function editPowerControlUnit(id, data) {
  return async (dispatch) => {
    try {
      const response = await RestService.editPowerControlUnit(id, data);
      if (response.data) {
        dispatch(updatePowerControlUnitOnState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function deletePowerControlUnit(id) {
  return async (dispatch) => {
    try {
      await RestService.deletePowerControlUnit(id);
      return true;
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function updateDevices(id, devices) {
  return async (dispatch) => {
    try {
      const response = await RestService.updatePowerControlUnitDevices(id, devices);
      if (response.data) {
        dispatch(updatePowerControlUnitOnState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function updateRules(id, rules) {
  return async (dispatch) => {
    try {
      const response = await RestService.updatePowerControlUnitRules(id, rules);
      if (response.data) {
        dispatch(updatePowerControlUnitOnState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function updateActuators(id, actuators) {
  return async (dispatch) => {
    try {
      const response = await RestService.updatePowerControlUnitActuators(id, actuators);
      if (response.data) {
        dispatch(updatePowerControlUnitOnState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function updateAutomation(id, enabled) {
  return async (dispatch) => {
    try {
      const response = await RestService.updatePowerControlUnitAutomation(id, enabled);
      if (response.data) {
        dispatch(updatePowerControlUnitOnState(response.data));
        return response.data;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function sendPowerCommand(id, state, lat, lng) {
  return async (dispatch) => {
    try {
      const response = await RestService.powerControlUnitCommand(id, state, lat, lng);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function fetchPowerControlUnitEvents(id, page = 0, append = false, pageSize = 50) {
  return async (dispatch, getState) => {
    try {
      const filters = getState().powerControlUnits.events.filters;
      const params = _.pickBy({
        page,
        pageSize,
        from: filters.fromDate,
        to: filters.toDate,
        eventType: filters.eventType,
      }, value => value !== null && value !== undefined);

      const response = await RestService.fetchPowerControlUnitEvents(id, params);

      if (response.data && response.data.content) {
        dispatch(append ? appendPowerControlUnitEvents(response.data.content) : savePowerControlUnitEvents(response.data.content));
        dispatch(setPowerControlUnitEventsPaginationData(_.omit(response.data, 'content')));
        return response.data.content;
      }
      throw new Error();
    } catch (error) {
      throw new Error(error);
    }
  };
}

export function fetchOverview() {
  return async (dispatch) => {
    const response = await RestService.fetchPowerControlUnitsOverview();
    if (!response.data) throw new Error();
    dispatch(savePowerControlUnitsOverview(response.data));
    return response.data;
  };
}

export function fetchPowerOnHours(fromDate, toDate) {
  return async (dispatch) => {
    const response = await RestService.fetchPowerControlUnitsPowerOnHours({ fromDate, toDate });
    if (!response.data) throw new Error();
    dispatch(savePowerControlUnitsPowerOnHours(response.data));
    return response.data;
  };
}

export function fetchEnergySummary(year) {
  return async (dispatch) => {
    const response = await RestService.fetchPowerControlUnitsEnergySummary({ year });
    if (!response.data) throw new Error();
    dispatch(savePowerControlUnitsEnergySummary(response.data));
    return response.data;
  };
}

export function fetchAlerts() {
  return async (dispatch) => {
    const response = await RestService.fetchPowerControlUnitsAlerts();
    if (!response.data) throw new Error();
    dispatch(savePowerControlUnitsAlerts(response.data));
    return response.data;
  };
}

export function exportReport(fromDate, toDate, format = 'pdf') {
  return async () => {
    const response = await RestService.exportPowerControlUnitsReport({ fromDate, toDate, format });
    if (!response.data) throw new Error();
    const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
    const fileName = `EnergyReport_${moment(fromDate).format('YYYY-MM-DD')}_${moment(toDate).format('YYYY-MM-DD')}.${format}`;
    FileSaver.saveAs(new Blob([response.data], { type: mimeType }), fileName);
  };
}
